/**
 * App Component
 * Main application with routing and layout — Riftbound TCG branding
 */

import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link } from 'react-router-dom';
import { fetchJson, getApiUrl } from './api.js';
import CardGallery from './CardGallery.jsx';
import CollectionView from './CollectionView.jsx';
import PackOpening from './PackOpening.jsx';
import DeckBuilderView from './DeckBuilderView.jsx';
import SavedDecksView from './SavedDecksView.jsx';
import DraftView from './DraftView.jsx';
import ProfileView from './ProfileView.jsx';
import SettingsView from './SettingsView.jsx';
import AdminView from './AdminView.jsx';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // Fetch current user on mount
  useEffect(() => {
    async function loadUser() {
      try {
        const data = await fetchJson('/api/me');
        setUser(data);
      } catch (err) {
        console.log('Not authenticated:', err.message);
        setUser(null);
      } finally {
        setLoading(false);
      }
    }

    loadUser();
  }, []);

  if (loading) {
    return (
      <div className="app-loading riftbound-loading">
        <div className="loading-logo" aria-hidden="true" />
        <p>Loading RiftVision...</p>
      </div>
    );
  }

  const avatarUrl =
    user && user.avatar && user.discord_id
      ? `https://cdn.discordapp.com/avatars/${user.discord_id}/${user.avatar}.png?size=64`
      : null;

  const userInitial =
    user && user.username ? user.username.charAt(0).toUpperCase() : '?';

  return (
    <BrowserRouter>
      <div className="app riftbound-app">
        {user?.isAdmin && (
          <div className="admin-banner" role="status">
            You are logged in as an admin. Admin actions can affect all users and data.
          </div>
        )}
        <header className="app-header riftbound-header">
          <div className="header-container">
            <Link to="/" className="app-brand">
              <span className="brand-logo" aria-hidden="true" />
              <span className="brand-text">
                <span className="brand-title">RiftVision</span>
                <span className="brand-subtitle">Riftbound TCG</span>
              </span>
            </Link>
            <nav className="app-nav">
              <Link to="/" className="nav-link">Gallery</Link>
              <Link to="/packs" className="nav-link">Packs</Link>
              <Link to="/collection" className="nav-link">Collection</Link>
              <Link to="/deck-builder" className="nav-link">Deck Builder</Link>
              <Link to="/draft" className="nav-link">Draft</Link>
            </nav>
            <div className="app-user">
              {user ? (
                <div className="user-menu">
                  <button
                    type="button"
                    className="user-menu-button"
                    onClick={() => setUserMenuOpen((open) => !open)}
                  >
                    <span className="user-avatar" aria-hidden="true">
                      {avatarUrl ? (
                        <img
                          src={avatarUrl}
                          alt=""
                          className="user-avatar-image"
                        />
                      ) : (
                        <span className="user-avatar-fallback">
                          {userInitial}
                        </span>
                      )}
                    </span>
                    <span className="user-name" title={user.username}>
                      {user.username}
                    </span>
                    <span className="user-menu-caret" aria-hidden="true">
                      ▾
                    </span>
                  </button>
                  {userMenuOpen && (
                    <div className="user-menu-dropdown">
                      <Link
                        to="/profile"
                        className="user-menu-item"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        Profile
                      </Link>
                      <Link
                        to="/saved-decks"
                        className="user-menu-item"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        Saved Decks
                      </Link>
                      <Link
                        to="/settings"
                        className="user-menu-item"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        Settings
                      </Link>
                      {user.isAdmin && (
                        <Link
                          to="/admin"
                          className="user-menu-item"
                          onClick={() => setUserMenuOpen(false)}
                        >
                          Admin
                        </Link>
                      )}
                      <button
                        type="button"
                        className="user-menu-item user-menu-item-danger"
                        onClick={async () => {
                          try {
                            await fetchJson('/api/auth/logout', { method: 'POST' });
                          } catch (e) {
                            // ignore, best-effort logout
                          } finally {
                            setUser(null);
                            setUserMenuOpen(false);
                          }
                        }}
                      >
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <span className="user-info">
                  <button
                    type="button"
                    className="auth-button"
                    onClick={() => {
                      window.location.href = getApiUrl('/api/auth/discord');
                    }}
                  >
                    Login with Discord
                  </button>
                </span>
              )}
            </div>
          </div>
        </header>

        <div className="riftbound-hero" role="banner">
          <div className="riftbound-hero-bg" />
          <div className="riftbound-hero-content">
            <span className="hero-domains" aria-hidden="true">
              <span className="domain-dot domain-fury" />
              <span className="domain-dot domain-calm" />
              <span className="domain-dot domain-body" />
              <span className="domain-dot domain-mind" />
              <span className="domain-dot domain-order" />
              <span className="domain-dot domain-chaos" />
            </span>
            <p className="hero-tagline">Collection · Decks · Packs · Draft</p>
          </div>
        </div>

        {/* Main Content */}
        <main className="app-main">
          <Routes>
            <Route path="/" element={<CardGallery />} />
            <Route path="/packs" element={<PackOpening />} />
            <Route path="/collection" element={<CollectionView />} />
            <Route path="/deck-builder" element={<DeckBuilderView />} />
            <Route path="/saved-decks" element={<SavedDecksView />} />
            <Route path="/draft" element={<DraftView />} />
            <Route path="/profile" element={<ProfileView />} />
            <Route path="/settings" element={<SettingsView />} />
            <Route path="/admin" element={<AdminView user={user} />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>

        {/* Footer with Riot acknowledgment */}
        <footer className="app-footer riftbound-footer">
          <p className="footer-brand">RiftVision — Riftbound TCG Collection Manager</p>
          <p className="footer-legal">
            Riftbound is a trademark of Riot Games, Inc. This site is not affiliated with Riot Games.
          </p>
          <p className="footer-links">
            <a href="https://www.riotgames.com/en/legal" target="_blank" rel="noopener noreferrer" className="footer-link">Legal (Riot)</a>
          </p>
        </footer>
      </div>
    </BrowserRouter>
  );
}

function NotFound() {
  return (
    <div className="not-found">
      <h2>404 - Page Not Found</h2>
      <p>The page you&apos;re looking for doesn&apos;t exist.</p>
      <Link to="/" className="back-link">← Back to Gallery</Link>
    </div>
  );
}

export default App;
